import {openDB} from 'idb';

let databasePromise;
async function database() {
    if (typeof indexedDB === 'undefined') throw new Error('Local storage unavailable');
    if (!databasePromise) {
        databasePromise = openDB('cos-chat-outbox', 1, {
            upgrade(db) {
                const store = db.createObjectStore('messages', {keyPath: 'key'});
                store.createIndex('by-conversation', ['scope', 'conversation_id']);
            },
            blocking() { void databasePromise?.then(db => db.close()); databasePromise = undefined; },
            terminated() { databasePromise = undefined; },
        }).catch(error => { databasePromise = undefined; throw error; });
    }
    return databasePromise;
}

const key = (scope, conversationId, clientId, rootId = 0) => `${scope}:${conversationId}:${rootId}:${clientId}`;
export function localClientId(message) {
    return message.client_message_id || (typeof message.id === 'string' && message.id.startsWith('pending-') ? message.id.slice(8) : '');
}

function userForStorage(user) {
    if (!user) return null;
    const {id, username, name, role} = user;
    return {id, username, name, role};
}

function attachmentForStorage(attachment) {
    const {id, original_filename, content_type, size_bytes, download_path, thumbnail_path} = attachment;
    return {id, original_filename, content_type, size_bytes, download_path, thumbnail_path};
}

export async function saveOutboxMessage(scope, message, {ifAbsent = false} = {}) {
    const clientId = localClientId(message);
    if (!scope || !clientId || !message.conversation_id) throw new Error('Cannot identify unsent message');
    const db = await database();
    const tx = db.transaction('messages', 'readwrite');
    const recordKey = key(scope, message.conversation_id, clientId, message.thread_root_message_id || 0);
    try {
        if (!ifAbsent || !await tx.store.get(recordKey)) {
            const quote = message.quoted_message;
            await tx.store.put({
                key: recordKey, scope, id: `pending-${clientId}`, client_message_id: clientId,
                conversation_id: message.conversation_id, thread_root_message_id: message.thread_root_message_id || null,
                sender: userForStorage(message.sender), message_type: 'USER', body: message.body, original_body: message.original_body,
                attachments: (message.attachments || []).map(attachmentForStorage),
                quoted_message: quote ? {id: quote.id, body: quote.body, sender: userForStorage(quote.sender), created_at: quote.created_at,
                    deleted: quote.deleted, version: quote.version, sequence: quote.sequence, thread_root_message_id: quote.thread_root_message_id,
                    attachments: (quote.attachments || []).map(attachmentForStorage)} : null,
                created_at: message.created_at, pending: false, send_error: true, reactions: [], reply_count: 0,
                sendError: message.sendError ? {name: message.sendError.name, message: message.sendError.message,
                    code: message.sendError.code, status: message.sendError.status} : undefined,
            });
        }
        await tx.done;
    } catch (error) {
        await tx.done.catch(() => undefined);
        throw error;
    }
}

export async function readOutboxMessages(scope, conversationId, rootId = 0) {
    if (!scope || !conversationId) return [];
    const records = await (await database()).getAllFromIndex('messages', 'by-conversation', [scope, conversationId]);
    return records.filter(message => (message.thread_root_message_id || 0) === rootId)
        .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)))
        .map(({key: ignoredKey, scope: ignoredScope, ...message}) => message);
}

export async function removeOutboxMessage(scope, conversationId, clientId, rootId = 0) {
    await (await database()).delete('messages', key(scope, conversationId, clientId, rootId));
}
